"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.getVariant = exports.defaultProps = exports.default = exports.ActionGroupDefault = void 0;
require("./ActionGroupDefault.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _actionFormatter = require("../../util/actionFormatter/actionFormatter");
var _getButtonHtml = require("../../util/getButtonHtml/getButtonHtml");
var _getLinkHtml = require("../../util/getLinkHtml/getLinkHtml");
var _renderObject = require("../../util/renderObject/renderObject");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * ActionGroupDefault component
 *
 * @method
 * @param { ActionGroupDefaultProps} props
 * @returns ActionGroupDefault
 */

const ActionGroupDefault = props => {
  const {
    action1,
    action2,
    derivedAriaLabel,
    externalAction1,
    externalAction2,
    fullyClickableParent = defaultProps.fullyClickableParent,
    isKnockoutLink,
    style = defaultProps.style,
    type = defaultProps.type
  } = props;
  if (!action1 && !action2 && !externalAction1 && !externalAction2) {
    return null;
  }
  let primaryAction = {};
  let secondaryAction = {};
  const useExternalActions = externalAction1 || externalAction2;
  const isArrowLink = type === 'Arrow Link';
  if (!useExternalActions) {
    let variant = getVariant(type);
    if (!!action1 && action1.label) {
      primaryAction = (0, _actionFormatter.actionFormatter)({
        action: action1,
        style: variant.action1,
        derivedAriaLabel: derivedAriaLabel,
        isKnockoutLink: isKnockoutLink,
        fullyClickableParent: fullyClickableParent
      }, {
        getButtonMarkup: _getButtonHtml.getButtonHtml,
        getLinkMarkup: _getLinkHtml.getLinkHtml
      });
    }
    if (!!action2 && action2.label) {
      secondaryAction = (0, _actionFormatter.actionFormatter)({
        action: action2,
        style: variant.action2,
        derivedAriaLabel: derivedAriaLabel,
        isKnockoutLink: isKnockoutLink,
        fullyClickableParent: fullyClickableParent
      }, {
        getButtonMarkup: _getButtonHtml.getButtonHtml,
        getLinkMarkup: _getLinkHtml.getLinkHtml
      });
    }
  }
  if (useExternalActions) {
    if (!!externalAction1) {
      primaryAction = externalAction1;
    }
    if (!!externalAction2) {
      secondaryAction = externalAction2;
    }
  }
  const componentClassName = (0, _classnames.default)('pal-c-ActionGroupDefault', {
    [`pal-c-ActionGroupDefault--${style}`]: style
  });
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName
  }, (0, _renderObject.renderObject)(primaryAction), !isArrowLink && (0, _renderObject.renderObject)(secondaryAction));
};

/**
 * Function for button action style
 * @function getVariant
 * @param { object } actionStyles - returns an object with two key/value pairs for action1 and action 2 styles
 */
exports.ActionGroupDefault = ActionGroupDefault;
const getVariant = type => {
  if (!type) {
    return;
  }
  let types = type.split('/');
  let actionStyles = {
    action1: types[0],
    action2: types[1]
  };
  return actionStyles;
};

/**
 * Property type definitions
 * @typedef ActionGroupDefaultProps
 * @type {object}
 * @property {string} type - string value that sets the style variants of the buttons and links
 * @property {string} style - string value that sets the layout the the ActionGroupDefault
 * @property {object} action1 - action1 object
 * @property {object} action2 - action2 object
 * @property {object} externalAction1 - external action1 object
 * @property {object} externalAction2 - external action2 object
 * @property {boolean} fullyClickableParent - =false - boolean value that sets whether the link is a fully clickable link for the element
 * @property {boolean} isKnockoutLink - sets the links to white when text is white
 */
exports.getVariant = getVariant;
const propTypes = exports.propTypes = {
  /**
   * Sets the stylistic version of the actions
   * - **Primary Button/Secondary Button:** Sets the actions to display as the Primary Button and Secondary Button
   * - **Primary Button/Tertiary Button:** Sets the actions to display as the Primary Button and Tertiary Button
   * - **Primary Button/Primary Link:** Sets the actions to display as the Primary Button and Primary Link
   * - **Secondary Button/Primary Link:** Sets the actions to display as the Secondary Button and Primary Link
   * - **Primary Button Inverse/Secondary Button:** Sets the actions to display as the Primary Button Inverse and Secondary Button
   * - **Primary Button Inverse/Tertiary Button:** Sets the actions to display as the Primary Button Inverse and Tertiary Button
   * - **Primary Button Inverse/Primary Link:** Sets the actions to display as the Primary Button Inverse and Primary Link
   * - **Primary Link/Primary Link:** Sets the actions to display as two Primary Links
   * - **Arrow Link:** Sets the link to a arrow style
   */
  type: _propTypes.default.oneOf(['Primary Button/Secondary Button', 'Primary Button/Tertiary Button', 'Primary Button/Primary Link', 'Secondary Button/Primary Link', 'Primary Button Inverse/Secondary Button', 'Primary Button Inverse/Tertiary Button', 'Primary Button Inverse/Primary Link', 'Primary Link/Primary Link', 'Arrow Link']),
  /**
   * Sets the layout of the actions
   * - **inline:** Sets the actions to display inline
   * - **stacked:** Sets the actions to display as block elements (flex column)
   * - **stackedFullWidth:** Sets the actions to display as block elements and also forces the buttons to the span full width of the parent container in all viewports. Links are centered but do not spand the full width.
   */
  style: _propTypes.default.oneOf(['inline', 'stackedDefault', 'stackedFullWidth']),
  /**
   * Sets the primary action (palette only, no business logic)
   */
  action1: _propTypes.default.object,
  /**
   * Sets the secondary action (palette only, no business logic)
   */
  action2: _propTypes.default.object,
  /**
   * Sets the primary action from an external source that contains business logic
   */
  externalAction1: _propTypes.default.object,
  /**
   * Sets the secondary action from an external source that contains business logic
   */
  externalAction2: _propTypes.default.object,
  /**
   * Sets the primary action (palette only, no business logic)
   */
  fullyClickableParent: _propTypes.default.bool,
  /**
   * Sets the links to white when text is white
   */
  isKnockoutLink: _propTypes.default.bool
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {boolean} fullyClickableParent - =false - boolean value that sets whether the link is a fully clickable link for the element
 * @property {string} type - string value that sets the style variants of the buttons and links
 * @property {string} style - string value that sets the layout the the ActionGroupDefault
 */
const defaultProps = exports.defaultProps = {
  fullyClickableParent: false,
  style: 'inline',
  type: 'Primary Button/Secondary Button'
};
ActionGroupDefault.propTypes = propTypes;
var _default = exports.default = ActionGroupDefault;